import React, { useRef, useState, useEffect } from 'react';
import { toPng } from 'html-to-image';
import { Download, Share2, X, Loader2 } from 'lucide-react';
import logo from '../assets/logo.jpg'; // Ensure this path is correct based on your previous work

const ShareCard = ({ user, books, onClose }) => {
  const cardRef = useRef(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);

  // Filter for top/recent books
  // Strategy: Take top rated "Read" books, or if rating is 0, take recently added "Read" books.
  const topBooks = books
    .filter(b => b.status === 'Read' || b.status === 'read')
    .sort((a, b) => (b.rating || 0) - (a.rating || 0)) // Sort by rating desc
    .slice(0, 3); // Take top 3

  const readCount = books.filter(b => b.status === 'Read' || b.status === 'read').length;
  const tbrCount = books.filter(b => b.status === 'TBR' || b.status === 'tbr').length;

  const handleDownload = async () => {
    if (cardRef.current === null) return;
    setIsGenerating(true);
    try {
      const dataUrl = await toPng(cardRef.current, { cacheBust: true, pixelRatio: 2 });
      const link = document.createElement('a');
      link.download = `leaf-and-letter-profile.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to generate image', err);
      alert("Could not generate image. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <div style={header}>
          <h3 style={{ margin: 0 }}>Share Your Library</h3>
          <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        </div>

        <div style={scrollArea}>
          {/* The Card to be Captured */}
          <div ref={cardRef} style={cardContainer}>
            {/* Header */}
            <div style={cardHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={logo} alt="L&L" style={{ width: '24px', borderRadius: '4px' }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#5d4037', letterSpacing: '1px' }}>LEAF & LETTER</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: '#8d6e63' }}>{new Date().toLocaleDateString()}</div>
            </div>

            {/* Title */}
            <div style={{ textAlign: 'center', margin: '20px 0' }}>
              <h2 style={{ fontFamily: 'Georgia, serif', color: '#3e2723', fontSize: '1.8rem', margin: 0 }}>
                {user.profile?.username || user.email.split('@')[0]}'s
              </h2>
              <div style={{ fontSize: '0.9rem', color: '#795548', fontStyle: 'italic' }}>Reading Sanctuary</div>
            </div>

            {/* Stats Row */}
            <div style={statsRow}>
              <div style={statItem}>
                <div style={statNumber}>{readCount}</div>
                <div style={statLabel}>READ</div>
              </div>
              <div style={divider} />
              <div style={statItem}>
                <div style={statNumber}>{tbrCount}</div>
                <div style={statLabel}>TBR</div>
              </div>
            </div>

            {/* Books Showcase */}
            <div style={{ padding: '0 20px 20px 20px' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#8d6e63', marginBottom: '10px', textAlign: 'center' }}>
                FAVORITES & RECENT READS
              </div>
              <div style={bookGrid}>
                {topBooks.length > 0 ? topBooks.map((book, i) => (
                  <div key={i} style={bookItem}>
                    <div style={coverPlaceholder}>
                      {book.cover ? (
                        <img src={book.cover} alt={book.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} crossOrigin="anonymous" />
                      ) : (
                        <div style={{ width: '100%', height: '100%', backgroundColor: '#d7ccc8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: '#5d4037', textAlign: 'center', padding: '2px' }}>
                          {book.title.slice(0, 10)}...
                        </div>
                      )}
                    </div>
                    {/* Stars */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1px', marginTop: '4px' }}>
                      {[...Array(5)].map((_, idx) => (
                        <div key={idx} style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: idx < (book.rating || 0) ? '#ffb74d' : '#e0e0e0' }} />
                      ))}
                    </div>
                  </div>
                )) : (
                  <p style={{ fontSize: '0.8rem', color: '#a1887f', fontStyle: 'italic', gridColumn: 'span 3', textAlign: 'center' }}>
                    No books finished yet. Time to start reading!
                  </p>
                )}
              </div>
            </div>

            {/* Footer */}
            <div style={cardFooter}>
              leafandletter.site
            </div>
          </div>
        </div>

        <div style={actions}>
          <button onClick={handleDownload} disabled={isGenerating} style={downloadBtn}>
            {isGenerating ? <Loader2 size={18} className="spin" /> : <Download size={18} />}
            {isGenerating ? ' Generating...' : ' Download Image'}
          </button>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', textAlign: 'center', marginTop: '10px' }}>
            Save this image to share on Instagram, Twitter, or Discord.
          </p>
        </div>
      </div>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 3000,
  padding: '20px'
};

const modalContent = {
  backgroundColor: 'var(--bg-card)',
  borderRadius: '16px',
  width: '100%',
  maxWidth: '400px',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh'
};

const header = {
  padding: '16px',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: 'var(--text-main)'
};

const closeBtn = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)'
};

const scrollArea = {
  overflowY: 'auto',
  padding: '20px',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: '#2c2c2c' // Dark background to make the light card pop
};

// The Card Design (Fixed styling to look good in PNG)
const cardContainer = {
  width: '300px',
  backgroundColor: '#f5f5f5', // Cream/Paper white
  backgroundImage: 'linear-gradient(to bottom right, #fdfbf7, #efebe9)',
  borderRadius: '8px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  overflow: 'hidden',
  position: 'relative',
  border: '1px solid #d7ccc8',
  fontFamily: 'serif'
};

const cardHeader = {
  padding: '16px 20px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  borderBottom: '1px dashed #d7ccc8'
};

const statsRow = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '15px 0',
  gap: '20px'
};

const statItem = {
  textAlign: 'center'
};

const statNumber = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: '#3e2723',
  lineHeight: '1'
};

const statLabel = {
  fontSize: '0.65rem',
  color: '#8d6e63',
  letterSpacing: '1px',
  marginTop: '4px'
};

const divider = {
  width: '1px',
  height: '30px',
  backgroundColor: '#d7ccc8'
};

const bookGrid = {
  display: 'flex',
  justifyContent: 'center',
  gap: '15px'
};

const bookItem = {
  width: '70px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const coverPlaceholder = {
  width: '70px',
  height: '105px',
  borderRadius: '4px',
  overflow: 'hidden',
  boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
  backgroundColor: '#eee'
};

const cardFooter = {
  backgroundColor: '#3e2723',
  color: '#efebe9',
  textAlign: 'center',
  padding: '8px',
  fontSize: '0.7rem',
  letterSpacing: '1px'
};

const actions = {
  padding: '16px',
  borderTop: '1px solid var(--border)',
  backgroundColor: 'var(--bg-card)'
};

const downloadBtn = {
  width: '100%',
  padding: '12px',
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px'
};

export default ShareCard;
